import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

// Format helpers
const fmtMoney = (cents, currency = "€") => {
  if (cents == null) return "—"
  const num = (cents / 100).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return `${num} ${currency}`
}

const fmtPercent = (n) => {
  if (n == null) return "—"
  return `${n.toFixed(2)} %`
}

const fmtMoneyOrPct = (row, value, currency) => {
  if (row.format === "percent") return fmtPercent(value)
  return fmtMoney(value, currency)
}

// Like fmtMoney but returns a discreet dash for true zeros
// (useful on multi-version budget columns that are often empty).
const fmtMoneyOptional = (cents, currency = "€") => {
  if (cents == null || cents === 0) return <span className="text-slate-300">—</span>
  return fmtMoney(cents, currency)
}

const signClass = (value) => {
  if (value == null) return ""
  if (value < 0)     return "text-rose-600"
  if (value > 0)     return ""
  return "text-slate-400"
}

const rowToneClass = (row) => {
  if (row.result)   return "bg-amber-50 border-y-2 border-amber-300 font-bold"
  if (row.emphasis) return "bg-slate-100 font-semibold"
  return ""
}

const indentClass = (indent) => {
  switch (indent) {
    case 0:  return "pl-3"
    case 1:  return "pl-8 text-slate-600 text-[12.5px]"
    default: return "pl-3"
  }
}

const Card = ({ title, children }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 mb-3">
    <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">{title}</h4>
    {children}
  </div>
)

const Pair = ({ label, value, tone = "" }) => (
  <div className="flex justify-between items-baseline text-[13px] py-1">
    <span className="text-slate-600">{label}</span>
    <span className={`tabular-nums font-medium ${tone}`}>{value}</span>
  </div>
)

const MargesGrid = ({ marges, base }) => {
  if (!marges || !marges[base]) return null
  const m = marges[base]
  const letter = { sec_a: "A", sec_b: "B", sec_c: "C" }[base]
  const label  = letter
    ? I18n.t("projects.analytics.cascade.margins_by_base", { base: letter })
    : I18n.t("projects.analytics.cascade.margins")
  return (
    <Card title={label}>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[12.5px] tabular-nums">
        <span className="text-slate-500">{I18n.t("projects.analytics.cascade.margin_1")}</span>          <span className="text-right">{m.marge_1?.toFixed(3)}</span>
        <span className="text-slate-500">{I18n.t("projects.analytics.cascade.margin_1_adj")}</span><span className="text-right">{m.marge_1_adj?.toFixed(3)}</span>
        <span className="text-slate-500">{I18n.t("projects.analytics.cascade.margin_2")}</span>          <span className="text-right">{m.marge_2?.toFixed(3)}</span>
        <span className="text-slate-500">{I18n.t("projects.analytics.cascade.margin_2_adj")}</span>         <span className="text-right">{m.marge_2_adj?.toFixed(3)}</span>
        <span className="text-slate-500">{I18n.t("projects.analytics.cascade.margin_3")}</span>          <span className="text-right">{m.marge_3?.toFixed(3)}</span>
        <span className="text-slate-500">{I18n.t("projects.analytics.cascade.margin_3_adj")}</span>         <span className="text-right">{m.marge_3_adj?.toFixed(3)}</span>
      </div>
    </Card>
  )
}

const CascadeTable = ({ rows, breakdown, currency, previousSnapshot }) => {
  const [openNodes, setOpenNodes] = useState({})

  const toggle = (nodeCode) => setOpenNodes(s => ({ ...s, [nodeCode]: !s[nodeCode] }))

  const m1Label = previousSnapshot?.period_label
    ? I18n.t("projects.analytics.cascade.projection_period", { period: previousSnapshot.period_label })
    : I18n.t("projects.analytics.cascade.projection_m1")

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px] tabular-nums" style={{ borderCollapse: "separate", borderSpacing: 0, minWidth: 1600 }}>
          <thead className="bg-slate-50">
            {/* Group headers */}
            <tr className="text-slate-500 text-[10px] uppercase tracking-wider">
              <th className="sticky left-0 bg-slate-50 z-10" rowSpan={2}></th>
              <th className="text-center py-2 px-2 bg-indigo-50 border-r border-slate-200" colSpan={4} style={{ color: "#4338ca" }}>
                {I18n.t("projects.analytics.cascade.groups.budgets")}
              </th>
              <th className="text-center py-2 px-2 border-r border-slate-200" colSpan={2}>{I18n.t("projects.analytics.cascade.groups.realization")}</th>
              <th className="text-center py-2 px-2 bg-emerald-50 border-r border-slate-200" colSpan={2} style={{ color: "#065f46" }}>
                {I18n.t("projects.analytics.cascade.groups.current_projection")}
              </th>
              <th className="text-center py-2 px-2 bg-amber-50" colSpan={2} style={{ color: "#92400e" }}>
                {I18n.t("projects.analytics.cascade.groups.comparison_m1")}
              </th>
            </tr>
            <tr className="text-slate-500 text-[10.5px] uppercase tracking-wider">
              <th className="text-left py-2 px-3 sticky left-0 bg-slate-50 z-10" style={{ minWidth: 280 }}>{I18n.t("projects.analytics.cascade.col.item")}</th>
              <th className="text-right py-2 px-2 bg-indigo-50/60"  title={I18n.t("projects.analytics.cascade.col.atoll_hint")}>ATOLL</th>
              <th className="text-right py-2 px-2 bg-indigo-50/60"  title={I18n.t("projects.analytics.cascade.col.fdv_initial_hint")}>{I18n.t("projects.analytics.cascade.col.fdv_initial")}</th>
              <th className="text-right py-2 px-2 bg-indigo-50/60"  title={I18n.t("projects.analytics.cascade.col.fdv_compliant_hint")}>{I18n.t("projects.analytics.cascade.col.fdv_compliant")}</th>
              <th className="text-right py-2 px-2 bg-indigo-50/60 border-r border-slate-200" title={I18n.t("projects.analytics.cascade.col.revised_target_hint")}>{I18n.t("projects.analytics.cascade.col.revised_target")}</th>
              <th className="text-right py-2 px-2">{I18n.t("projects.analytics.cascade.col.realized")}</th>
              <th className="text-right py-2 px-2 border-r border-slate-200">{I18n.t("projects.analytics.cascade.col.grand_total")}</th>
              <th className="text-right py-2 px-2 bg-emerald-50/60">{I18n.t("projects.analytics.cascade.col.provisional")}</th>
              <th className="text-right py-2 px-2 bg-emerald-50/60 border-r border-slate-200">{I18n.t("projects.analytics.cascade.col.budget_variance")}</th>
              <th className="text-right py-2 px-2 bg-amber-50/60">{m1Label}</th>
              <th className="text-right py-2 px-2 bg-amber-50/60">{I18n.t("projects.analytics.cascade.col.delta_m1")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <React.Fragment key={idx}>
                <tr className={rowToneClass(row)}>
                  <td className={`py-1.5 sticky left-0 ${rowToneClass(row) || "bg-white"} ${indentClass(row.indent)}`}>
                    {row.node_code && (
                      <button
                        type="button"
                        onClick={() => toggle(row.node_code)}
                        className="text-slate-400 hover:text-slate-700"
                        style={{ marginRight: "12px" }}
                        aria-label={I18n.t("projects.analytics.cascade.toggle")}
                      >
                        <FontAwesomeIcon icon={openNodes[row.node_code] ? "minus-square" : "plus-square"} />
                      </button>
                    )}
                    {row.label}
                  </td>
                  {/* Multi-version budgets */}
                  <td className="text-right py-1.5 px-2 bg-indigo-50/30 text-slate-700">{fmtMoneyOptional(row.budget_atoll, currency)}</td>
                  <td className="text-right py-1.5 px-2 bg-indigo-50/30 text-slate-700">{fmtMoneyOptional(row.budget_fdv_initial, currency)}</td>
                  <td className="text-right py-1.5 px-2 bg-indigo-50/30 text-slate-900 font-medium">{fmtMoneyOptional(row.budget_fdv_conforme, currency)}</td>
                  <td className="text-right py-1.5 px-2 bg-indigo-50/30 text-slate-700 border-r border-slate-200">{fmtMoneyOptional(row.budget_objectif_revise, currency)}</td>
                  {/* Realization */}
                  <td className={`text-right py-1.5 px-2 ${signClass(row.total_realized)}`}>
                    {fmtMoneyOrPct(row, row.total_realized, currency)}
                  </td>
                  <td className="text-right py-1.5 px-2 border-r border-slate-200">{fmtMoney(row.total_general, currency)}</td>
                  {/* Current projection */}
                  <td className="text-right py-1.5 px-2 bg-emerald-50/30">{fmtMoney(row.provisional, currency)}</td>
                  <td className={`text-right py-1.5 px-2 bg-emerald-50/30 border-r border-slate-200 ${signClass(row.budget_variance)}`}>
                    {fmtMoney(row.budget_variance, currency)}
                  </td>
                  {/* M-1 */}
                  <td className="text-right py-1.5 px-2 bg-amber-50/30 text-slate-700">{fmtMoneyOptional(row.projection_m_minus_1, currency)}</td>
                  <td className={`text-right py-1.5 px-2 bg-amber-50/30 ${signClass(row.delta_vs_m_minus_1)}`}>
                    {fmtMoneyOptional(row.delta_vs_m_minus_1, currency)}
                  </td>
                </tr>

                {row.node_code && openNodes[row.node_code] && (breakdown[row.node_code] || []).map((elt) => (
                  <tr key={`${row.node_code}-${elt.id}`} className="bg-slate-50/60 text-[12px] text-slate-600">
                    <td className="py-1 pl-14 sticky left-0 bg-slate-50/60">
                      <span className="font-mono text-[11px] mr-2 text-slate-400">{elt.code}</span>
                      {elt.name}
                    </td>
                    <td colSpan={4} className="bg-indigo-50/10 border-r border-slate-200"></td>
                    <td className="text-right py-1 px-2">{fmtMoney(elt.amount_cents, currency)}</td>
                    <td colSpan={5}></td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const AnalyticsCascadeSection = ({ analytics, project }) => {
  if (!analytics || !analytics.available) return null

  const { header, gestion, commandes, marges, cascade, breakdown, meta, ventilation_avenants } = analytics
  const currency = meta?.currency_symbol || "€"
  const previousSnapshot = cascade?.previous_snapshot

  const captureSnapshot = () => {
    if (!project?.id) return
    if (!confirm(I18n.t("projects.analytics.cascade.snapshot.confirm"))) return
    fetch(`/projects/${project.id}/monthly_snapshots`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content"),
      },
      credentials: "same-origin",
    })
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          alert(I18n.t("projects.analytics.cascade.snapshot.saved", { period: d.period_label }))
          window.location.reload()
        } else {
          alert(I18n.t("projects.analytics.cascade.snapshot.error", { errors: (d.errors || []).join(", ") }))
        }
      })
      .catch(() => alert(I18n.t("projects.analytics.cascade.snapshot.network_error")))
  }

  const editProvisionRae = () => {
    if (!project?.id) return
    const currentRow  = cascade?.rows?.find(r => r.editable === "provision_rae")
    const currentEur  = currentRow ? (currentRow.total_realized / 100).toFixed(2).replace(".", ",") : "0,00"
    const userInput   = prompt(I18n.t("projects.analytics.cascade.rae.prompt"), currentEur)
    if (userInput === null) return  // cancelled

    fetch(`/projects/${project.id}/provision_rae`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content"),
      },
      credentials: "same-origin",
      body: JSON.stringify({ amount_eur: userInput }),
    })
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          window.location.reload()
        } else {
          alert(I18n.t("projects.analytics.cascade.rae.error", { errors: (d.errors || [I18n.t("projects.analytics.cascade.rae.not_saved")]).join(", ") }))
        }
      })
      .catch(() => alert(I18n.t("projects.analytics.cascade.rae.network_error")))
  }

  return (
    <section id="section-analytics" className="mx-3 scroll-mt-24">
      <div className="bg-gradient-to-br from-amber-50 to-white border border-amber-200 rounded-xl p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[16px] font-bold text-amber-900 -tracking-[0.01em]">
            <FontAwesomeIcon icon="chart-line" className="mr-2 text-amber-600" />
            {I18n.t("projects.analytics.cascade.title")}
          </h3>
          <div className="flex items-center gap-2">
            {header?.code && (
              <span className="text-[12px] font-mono text-amber-800 bg-amber-100 px-2 py-0.5 rounded">
                {header.code}
              </span>
            )}
            <button
              type="button"
              onClick={editProvisionRae}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white hover:bg-amber-50 text-amber-700 border border-amber-300 text-[11.5px] font-medium shadow-sm"
              title={I18n.t("projects.analytics.cascade.rae.hint")}
            >
              <FontAwesomeIcon icon="shield-alt" />
              {I18n.t("projects.analytics.cascade.rae.action")}
            </button>
            <button
              type="button"
              onClick={captureSnapshot}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-[11.5px] font-medium shadow-sm"
              title={I18n.t("projects.analytics.cascade.snapshot.hint")}
            >
              <FontAwesomeIcon icon="camera" />
              {I18n.t("projects.analytics.cascade.snapshot.action")}
            </button>
          </div>
        </div>
        <p className="text-[12px] text-amber-800/80">
          {I18n.t("projects.analytics.cascade.subtitle")}
          {previousSnapshot && (
            <span className="ml-2 text-amber-700">
              · {I18n.t("projects.analytics.cascade.snapshot.last")} <strong>{previousSnapshot.period_label}</strong>
            </span>
          )}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
        <Card title={I18n.t("projects.analytics.cascade.cards.project")}>
          <Pair label={I18n.t("projects.analytics.cascade.cards.designation")} value={header?.designation} />
          <Pair label={I18n.t("projects.status")} value={header?.status} />
          <Pair label={I18n.t("projects.analytics.cascade.cards.project_manager")} value={header?.project_manager} />
          <Pair label={I18n.t("projects.analytics.cascade.cards.client")} value={header?.client_name} />
          <Pair label={I18n.t("projects.site")} value={header?.site_name} />
        </Card>

        <Card title={I18n.t("projects.analytics.cascade.cards.analysis")}>
          <Pair label={I18n.t("projects.analytics.cascade.cards.department")} value={header?.department} />
          <Pair label={I18n.t("projects.summary.trade")} value={header?.trade} />
          <Pair label={I18n.t("projects.analytics.cascade.cards.specialty")} value={header?.specialty} />
          <Pair label={I18n.t("projects.analytics.cascade.cards.start")} value={header?.start_date} />
          <Pair label={I18n.t("projects.analytics.cascade.cards.end")} value={header?.end_date} />
        </Card>

        <Card title={I18n.t("projects.analytics.cascade.cards.management")}>
          <Pair label={I18n.t("projects.analytics.cascade.cards.requests_excl_tax")} value={fmtMoney(gestion?.demandes_ht, currency)} />
          <Pair label={I18n.t("projects.analytics.cascade.cards.receipts_excl_tax")} value={fmtMoney(gestion?.recettes_ht, currency)} />
          <Pair label={I18n.t("projects.analytics.cascade.cards.balance_excl_tax")} value={fmtMoney(gestion?.solde_ht, currency)}
                tone={signClass(gestion?.solde_ht)} />
        </Card>

        <Card title={I18n.t("projects.analytics.cascade.cards.orders")}>
          <Pair label={I18n.t("projects.analytics.cascade.cards.base_orders")} value={fmtMoney(commandes?.os_base_cents, currency)} />
          <Pair label={I18n.t("projects.analytics.cascade.cards.extra_with_order")} value={fmtMoney(commandes?.ts_avec_os_cents, currency)} />
          <Pair label={I18n.t("projects.analytics.cascade.cards.extra_pending")} value={fmtMoney(commandes?.ts_en_attente_os_cents, currency)}
                tone={commandes?.alert_ts_en_attente ? "text-rose-600 font-semibold" : ""} />
          <Pair label={I18n.t("projects.analytics.cascade.cards.total_orders")} value={fmtMoney(commandes?.total_os_cents, currency)} />
        </Card>
      </div>

      {commandes?.alert_ts_en_attente && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 mb-3 text-[12.5px] text-rose-700">
          <FontAwesomeIcon icon="exclamation-triangle" className="mr-1.5" />
          <strong>{I18n.t("projects.analytics.cascade.extra_pending_alert.title")}</strong> {I18n.t("projects.analytics.cascade.extra_pending_alert.body")}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <MargesGrid marges={marges} base="sec_a" />
        <MargesGrid marges={marges} base="sec_b" />
        <MargesGrid marges={marges} base="sec_c" />
      </div>

      <CascadeTable
        rows={cascade?.rows || []}
        breakdown={breakdown || {}}
        currency={currency}
        previousSnapshot={previousSnapshot}
      />

      <VentilationAvenants data={ventilation_avenants} currency={currency} />
    </section>
  )
}

// FB BALAS #4 — expense allocation by amendment (TS). Displays
// nothing as long as there is neither an attached amendment nor allocated expenses.
const VentilationAvenants = ({ data, currency }) => {
  if (!data) return null
  const hasAvenants = (data.avenants || []).length > 0
  const hasBase     = data.base?.total_cents > 0
  if (!hasAvenants && !hasBase) return null

  const pctOf = (part, total) => {
    if (!total || total === 0) return ""
    return `${((part / total) * 100).toFixed(1)} %`
  }
  const grandTotal = data.total?.total_cents || 0

  return (
    <div className="mt-4 bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
        <FontAwesomeIcon icon="layer-group" className="text-slate-500" />
        <h3 className="text-[13px] font-semibold text-slate-800">{I18n.t("projects.analytics.cascade.ventilation.title")}</h3>
        <span className="text-[11.5px] text-slate-500 ml-1">{I18n.t("projects.analytics.cascade.ventilation.subtitle")}</span>
      </div>
      <table className="w-full text-[12.5px] tabular-nums">
        <thead className="bg-slate-50/60 text-[10.5px] uppercase tracking-wider text-slate-500">
          <tr>
            <th className="text-left py-2 px-4">{I18n.t("projects.analytics.cascade.ventilation.source")}</th>
            <th className="text-right py-2 px-2">{I18n.t("projects.analytics.cascade.col.realized")}</th>
            <th className="text-right py-2 px-2">{I18n.t("projects.analytics.cascade.ventilation.committed")}</th>
            <th className="text-right py-2 px-2">{I18n.t("projects.analytics.cascade.ventilation.total")}</th>
            <th className="text-right py-2 px-4">{I18n.t("projects.analytics.cascade.ventilation.share")}</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-slate-100">
            <td className="py-2 px-4 text-slate-700">{I18n.t("projects.analytics.cascade.ventilation.base_contract")}</td>
            <td className="text-right py-2 px-2">{fmtMoney(data.base.realized_cents, currency)}</td>
            <td className="text-right py-2 px-2">{fmtMoney(data.base.committed_cents, currency)}</td>
            <td className="text-right py-2 px-2 font-medium">{fmtMoney(data.base.total_cents, currency)}</td>
            <td className="text-right py-2 px-4 text-slate-500">{pctOf(data.base.total_cents, grandTotal)}</td>
          </tr>
          {(data.avenants || []).map(a => (
            <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50/40">
              <td className="py-2 px-4 text-slate-700">
                <span className="font-mono text-[11.5px] text-slate-500 mr-2">{a.number}</span>
                {a.title}
                {a.status && (
                  <span className={`ml-2 text-[10.5px] px-1.5 py-0.5 rounded ${a.status === "signed" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                    {a.status}
                  </span>
                )}
              </td>
              <td className="text-right py-2 px-2">{fmtMoney(a.realized_cents, currency)}</td>
              <td className="text-right py-2 px-2">{fmtMoney(a.committed_cents, currency)}</td>
              <td className="text-right py-2 px-2 font-medium">{fmtMoney(a.total_cents, currency)}</td>
              <td className="text-right py-2 px-4 text-slate-500">{pctOf(a.total_cents, grandTotal)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-slate-100 font-semibold">
            <td className="py-2.5 px-4">{I18n.t("projects.analytics.cascade.ventilation.total")}</td>
            <td className="text-right py-2.5 px-2">{fmtMoney(data.total.realized_cents, currency)}</td>
            <td className="text-right py-2.5 px-2">{fmtMoney(data.total.committed_cents, currency)}</td>
            <td className="text-right py-2.5 px-2">{fmtMoney(data.total.total_cents, currency)}</td>
            <td className="text-right py-2.5 px-4 text-slate-600">100 %</td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

export default AnalyticsCascadeSection
